import React, { useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar, Building, Users, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import atriumSezanaImage from '@/assets/atrium-sezana.png';
import klinikaSezanaImage from '@/assets/klinika-sezana.jpg';
import atriumRender1 from '@/assets/atrium-render-1.png';
import atriumRender2 from '@/assets/atrium-render-2.jpg';
import atriumRender3 from '@/assets/atrium-render-3.png';
import atriumRender4 from '@/assets/atrium-render-4.jpg';
import atriumRender5 from '@/assets/atrium-render-5.png';

const ProjectDetail = () => {
  const { slug } = useParams();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  // Project data
  const projects = {
    'atrium-sezana': {
      title: "Atrium Sežana",
      location: "Center Sežane",
      year: "2026",
      status: "V gradnji",
      type: "Butična stanovanja",
      description: "Butična stanovanja v kraškem slogu - kjer tradicija sreča sodobnost. V srcu Sežane stoji stara kraška hiša iz kamna, ki nosi v sebi zgodbe preteklih stoletij. Z največjo pozornostjo do detajlov preoblikujemo staro kraško hišo v 8 butičnih stanovanj.",
      details: {
        units: "8 unikatnih stanovanj",
        location: "Center Sežane",
        completion: "2026",
        priceRange: "160.000€ - 270.000€",
        features: [
          "Tradicionalni kraški 'borjač', kamnite stopnice, kamnit tlak, suhozidi",
          "Celovita prenova z prenovljeno streho in fasado",
          "Fasada obnovljena v skladu z ZVKDjem",
          "Nove instalacije (vodovodne, kanalizacijske, električne)",
          "Ogrevanje s toplotno črpalko (talno ogrevanje)",
          "Klimatske naprave v vseh stanovanjih",
          "Les-alu okna",
          "4 stanovanja v pritličju",
          "4 stanovanja v nadstropju",
          "5 lastniških teras",
          "Skupno dvorišče - atrium",
          "Ekskluzivnost - samo 8 stanovanj",
          "Nizki obratovalni stroški"
        ]
      },
      images: [
        atriumSezanaImage,
        atriumRender1,
        atriumRender2,
        atriumRender3,
        atriumRender4,
        atriumRender5
      ]
    },
    'hise-lokev': {
      title: "Hiše Lokev",
      location: "Lokev, Slovenija",
      year: "2026",
      status: "Pridobljeno GD",
      type: "Stanovanjske hiše",
      description: "3 stanovanjske enote od 130m² do 145m² na odlični lokaciji v Lokvi. Pridobljeno gradbeno dovoljenje, gradnja bo potekala v letu 2026 z zaključkom v istem letu.",
      details: {
        units: "3 stanovanjske enote (130-145m²)",
        location: "Lokev",
        completion: "2026",
        features: [
          "3 stanovanjske enote od 130m² do 145m²",
          "Pridobljeno gradbeno dovoljenje",
          "Gradnja v letu 2026",
          "Zaključek gradnje v letu 2026",
          "Odlična lokacija v Lokvi",
          "Kraška arhitektura",
          "Kvalitetni materiali",
          "Lastni vrt pri vsaki hiši",
          "Garaža",
          "Sodobna arhitektura",
          "Energetsko učinkovita gradnja"
        ]
      },
      images: [
        "/src/assets/hise-lokev-day.jpeg",
        "/src/assets/hise-lokev-night.png",
        "https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=1200&h=800&fit=crop"
      ]
    },
    'poslovni-objekt-sezana': {
      title: "Poslovni objekt Sežana",
      location: "Sežana, Slovenija",
      year: "2026-2027",
      status: "V pripravi",
      type: "Poslovni kompleks",
      description: "Ambiciozen projekt v centru Sežane, ki bo ponujal mešanico poslovnih dejavnosti - od zdravstvenih storitev do trgovine in ekskluzivnih stanovanjskih enot. Dokumentacija je predana za pridobitev gradbenega dovoljenja.",
      details: {
        units: "Klinika, trgovina, poslovni apartmaji, penthouse",
        location: "Center Sežane",
        completion: "2026",
        size: "cca 2000m² bruto",
        features: [
          "Klinika za zdravstvene storitve",
          "Trgovski prostori",
          "Poslovni apartmaji",
          "Luksuzni penthouse",
          "Parkirni prostori",
          "Centralna lokacija"
        ]
      },
      images: [
        klinikaSezanaImage,
        "https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&h=800&fit=crop",
        "https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=1200&h=800&fit=crop"
      ]
    },
    'poslovni-prostori-lokev': {
      title: "Poslovni prostori Lokev",
      location: "Lokev, Slovenija",
      year: "2024",
      status: "Prodano",
      type: "Obrtni prostori",
      description: "Hala z manjšimi enotami za obrtnike predstavlja odličen projekt za razvoj lokalnega podjetništva v Lokvi. Projekt je trenutno v fazi projektiranja z namenom ustvaritve sodobnih obrtnih prostorov.",
      details: {
        units: "Hala z obrtnimi enotami",
        location: "Lokev",
        completion: "2026",
        features: [
          "Različne velikosti obrtnih enot",
          "Dostop za tovorna vozila",
          "Parkirni prostori",
          "Sodobna infrastruktura",
          "Idealno za obrtnike",
          "Fleksibilni prostori"
        ]
      },
      images: [
        "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=1200&h=800&fit=crop",
        "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=1200&h=800&fit=crop",
        "https://images.unsplash.com/photo-1582735689369-4fe89db7114c?w=1200&h=800&fit=crop"
      ]
    }
  };

  const project = projects[slug as keyof typeof projects];

  if (!project) {
    return <Navigate to="/404" replace />;
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'V gradnji':
        return 'bg-atren-orange/10 text-atren-orange';
      case 'Pridobljeno GD':
        return 'bg-green-100 text-green-800';
      case 'V pripravi':
        return 'bg-blue-100 text-blue-800';
      case 'V projektiranju':
        return 'bg-purple-100 text-purple-800';
      case 'Prodano':
        return 'bg-gray-200 text-gray-600';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src={project.images[0]}
              alt={project.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-hero opacity-80"></div>
          </div>

          <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="max-w-4xl mx-auto">
              <Button
                variant="ghost"
                onClick={() => window.history.back()}
                className="mb-6 text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Nazaj na projekte
              </Button>
              
              <Badge className={getStatusColor(project.status)}>
                {project.status}
              </Badge>
              
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 mt-4">
                {project.title}
              </h1>
              
              <div className="flex items-center justify-center gap-6 text-white/90 mb-8">
                <div className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  {project.location}
                </div>
                <div className="flex items-center">
                  <Calendar className="w-5 h-5 mr-2" />
                  {project.year}
                </div>
                <div className="flex items-center">
                  <Building className="w-5 h-5 mr-2" />
                  {project.type}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Project Details */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                {/* Project Info */}
                <div>
                  <h2 className="text-3xl font-bold text-foreground mb-6">
                    O projektu
                  </h2>
                  <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                    {project.description}
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                    <Card>
                      <CardContent className="p-6">
                        <div className="flex items-center mb-2">
                          <Building className="w-5 h-5 text-atren-orange mr-2" />
                          <span className="font-semibold">Obseg</span>
                        </div>
                        <p className="text-muted-foreground">{project.details.units}</p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardContent className="p-6">
                        <div className="flex items-center mb-2">
                          <Calendar className="w-5 h-5 text-atren-orange mr-2" />
                          <span className="font-semibold">Dokončanje</span>
                        </div>
                        <p className="text-muted-foreground">{project.details.completion}</p>
                      </CardContent>
                    </Card>
                    
                    {'priceRange' in project.details && (
                      <Card className="sm:col-span-2">
                        <CardContent className="p-6">
                          <div className="flex items-center mb-2">
                            <Users className="w-5 h-5 text-atren-orange mr-2" />
                            <span className="font-semibold">Cenovno območje</span>
                          </div>
                          <p className="text-muted-foreground">{project.details.priceRange}</p>
                        </CardContent>
                      </Card>
                    )}

                    {'size' in project.details && (
                      <Card className="sm:col-span-2">
                        <CardContent className="p-6">
                          <div className="flex items-center mb-2">
                            <Users className="w-5 h-5 text-atren-orange mr-2" />
                            <span className="font-semibold">Velikost</span>
                          </div>
                          <p className="text-muted-foreground">{project.details.size}</p>
                        </CardContent>
                      </Card>
                    )}
                  </div>

                  <h3 className="text-xl font-semibold text-foreground mb-4">
                    Ključne značilnosti
                  </h3>
                  <ul className="space-y-2">
                    {project.details.features.map((feature, index) => (
                      <li key={index} className="flex items-center text-muted-foreground">
                        <div className="w-2 h-2 bg-atren-orange rounded-full mr-3"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Project Images */}
                <div className="space-y-6">
                  {project.images.slice(1).map((image, index) => (
                    <div 
                      key={index} 
                      className="rounded-lg overflow-hidden cursor-pointer"
                      onClick={() => setSelectedImage(image)}
                    >
                      <img
                        src={image}
                        alt={`${project.title} - slika ${index + 2}`}
                        className="w-full h-64 object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Lightbox Dialog */}
              <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
                <DialogContent className="max-w-[95vw] max-h-[95vh] w-auto h-auto p-2 border-0 bg-black/90 pointer-events-auto">
                  <img
                    src={selectedImage || ''}
                    alt="Povečana slika projekta"
                    className="max-w-full max-h-[90vh] object-contain rounded-lg"
                  />
                </DialogContent>
              </Dialog>

              {/* CTA Section */}
              <div className="mt-16 text-center">
                <Card className="bg-gradient-subtle p-8 max-w-2xl mx-auto">
                  <CardContent className="p-0">
                    <h3 className="text-2xl font-bold text-foreground mb-4">
                      Zanima vas ta projekt?
                    </h3>
                    <p className="text-muted-foreground mb-6">
                      Stopite v stik z nami za več informacij o tem projektu ali razpoložljivih enotah.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      {slug === 'atrium-sezana' && (
                        <Button
                          asChild
                          size="lg"
                          className="bg-gradient-primary shadow-primary hover:shadow-elegant transition-all duration-300"
                        >
                          <Link to="/atrium-sezana">
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Več o projektu Atrium
                          </Link>
                        </Button>
                      )}
                      <Button
                        size="lg"
                        variant={slug === 'atrium-sezana' ? 'outline' : 'default'}
                        onClick={() => {
                          window.location.href = '/#kontakt';
                        }}
                        className={slug === 'atrium-sezana' 
                          ? "border-atren-orange text-atren-orange hover:bg-atren-orange hover:text-white transition-all duration-300"
                          : "bg-gradient-primary shadow-primary hover:shadow-elegant transition-all duration-300"
                        }
                      >
                        Kontaktirajte nas
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ProjectDetail;